import { RenovationProject, ProjectStatus } from 'shared/types/renovation';

export interface ProjectPhase {
    key: string;
    label: string;
    projects: RenovationProject[];
    color: string;
    bgColor: string;
    borderColor: string;
}

export interface YearGroup {
    year: number;
    phases: ProjectPhase[];
    totalProjects: number;
    isCurrentYear: boolean;
}

export interface OrganizedProjects {
    years: YearGroup[];
    warnings: ProjectWarning[];
}

export interface ProjectWarning {
    projectId: number;
    projectTitle: string;
    type: 'status_date_mismatch';
    message: string;
    severity: 'warning' | 'error';
}

// Project statuses that indicate completion
const COMPLETED_STATUSES = [ProjectStatus.COMPLETED, ProjectStatus.PAID];

// Get the end date from project dates
const getProjectEndDate = (project: RenovationProject): Date | null => {
    if (!project.dates) return null;
    
    const endDates = project.dates
        .filter(d => d.type === 'Verwachte einddatum' && !d.isDeprecated)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    return endDates.length > 0 ? new Date(endDates[0].date) : null;
};

// Determine project phase based on end date and current date
const getProjectPhase = (endDate: Date | null, currentDate: Date): 'past' | 'present' | 'future' => {
    if (!endDate) return 'present'; // No end date = present
    
    const currentYear = currentDate.getFullYear();
    const endYear = endDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const endMonth = endDate.getMonth();
    
    // Same year - check if first or second half
    if (endYear === currentYear) {
        // If end date is in the past
        if (endDate < currentDate) return 'past';
        // If end date is in first half of year (Jan-Jun) and we're in second half
        if (endMonth < 6 && currentMonth >= 6) return 'past';
        // If end date is in second half and we're in first half, or same half
        return 'present';
    }
    
    // Different year
    if (endYear < currentYear) return 'past';
    return 'future';
};

// Check for project warnings
const checkProjectWarnings = (project: RenovationProject, endDate: Date | null, currentDate: Date): ProjectWarning[] => {
    const warnings: ProjectWarning[] = [];
    
    if (endDate && endDate < currentDate && !COMPLETED_STATUSES.includes(project.status)) {
        warnings.push({
            projectId: project.id,
            projectTitle: project.title,
            type: 'status_date_mismatch',
            message: `Project einddatum (${endDate.toLocaleDateString('nl-BE')}) ligt in het verleden maar status is "${project.status}" in plaats van "Afgerond" of "Volledig gefactureerd"`,
            severity: 'warning'
        });
    }
    
    return warnings;
};

// Organize projects by year and phase
export const organizeProjectsByYear = (projects: RenovationProject[]): OrganizedProjects => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const yearGroups: Map<number, Map<string, RenovationProject[]>> = new Map();
    const warnings: ProjectWarning[] = [];
    
    // Group projects by year and phase
    projects.forEach(project => {
        const endDate = getProjectEndDate(project);
        const projectYear = endDate ? endDate.getFullYear() : currentYear;
        const phase = getProjectPhase(endDate, currentDate);
        
        // Add warnings
        warnings.push(...checkProjectWarnings(project, endDate, currentDate));
        
        // Initialize year group if needed
        if (!yearGroups.has(projectYear)) {
            yearGroups.set(projectYear, new Map([
                ['past', []],
                ['present', []],
                ['future', []]
            ]));
        }
        
        yearGroups.get(projectYear)!.get(phase)!.push(project);
    });
    
    // Convert to organized structure
    const years: YearGroup[] = Array.from(yearGroups.entries())
        .map(([year, phaseMap]) => {
            const currentMonth = currentDate.getMonth();
            const isCurrentYear = year === currentYear;
            
            const phases: ProjectPhase[] = [];
            
            // Past phase (first half of year or previous years)
            const pastProjects = phaseMap.get('past') || [];
            if (pastProjects.length > 0) {
                const phaseLabel = isCurrentYear && currentMonth >= 6 
                    ? `${year} - Eerste helft` 
                    : `${year} - Verleden`;
                
                phases.push({
                    key: 'past',
                    label: phaseLabel,
                    projects: pastProjects.sort((a, b) => b.id - a.id),
                    color: 'text-neutral-600',
                    bgColor: 'bg-neutral-50',
                    borderColor: 'border-neutral-200'
                });
            }
            
            // Present phase (current period)
            const presentProjects = phaseMap.get('present') || [];
            if (presentProjects.length > 0) {
                const phaseLabel = isCurrentYear 
                    ? `${year} - Heden & Toekomst`
                    : `${year} - Heden`;
                
                phases.push({
                    key: 'present',
                    label: phaseLabel,
                    projects: presentProjects.sort((a, b) => b.id - a.id),
                    color: 'text-moss-primary',
                    bgColor: 'bg-moss-primary/5',
                    borderColor: 'border-moss-primary/30'
                });
            }
            
            // Future phase (future years only)
            const futureProjects = phaseMap.get('future') || [];
            if (futureProjects.length > 0 && !isCurrentYear) {
                phases.push({
                    key: 'future',
                    label: `${year} - Toekomst`,
                    projects: futureProjects.sort((a, b) => b.id - a.id),
                    color: 'text-blue-600',
                    bgColor: 'bg-blue-50',
                    borderColor: 'border-blue-200'
                });
            }
            
            const totalProjects = pastProjects.length + presentProjects.length + futureProjects.length;
            
            return {
                year,
                phases,
                totalProjects,
                isCurrentYear
            };
        })
        .filter(yearGroup => yearGroup.totalProjects > 0)
        .sort((a, b) => b.year - a.year); // Most recent years first
    
    return { years, warnings };
};

// Get phase indicator for a project
export const getProjectPhaseInfo = (project: RenovationProject) => {
    const currentDate = new Date();
    const endDate = getProjectEndDate(project);
    const phase = getProjectPhase(endDate, currentDate);
    const warnings = checkProjectWarnings(project, endDate, currentDate);
    
    return {
        phase,
        endDate,
        hasWarning: warnings.length > 0,
        warnings
    };
};

// Get project year from end date
export const getProjectYear = (project: RenovationProject): number => {
    const endDate = getProjectEndDate(project);
    return endDate ? endDate.getFullYear() : new Date().getFullYear();
};